import React, { useState, useEffect, useRef } from "react";
import { Button } from "components/Graphs/Button";
import { Textarea } from "components/Graphs/TextArea";
import { Input } from "components/Graphs/Input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "components/Graphs/Dialogue";
import { InfoBox } from "components/Graphs/InfoBox";
import { AlertCircleIcon } from "lucide-react";
import { RocketLaunchIcon } from "@heroicons/react/24/outline";
import agent, {
  LaunchableFromList,
  ModifyLaunchableRequest,
  LaunchableFileRequest,
} from "server";
import { toast } from "utils/toast";
import { useQueryClient } from "@tanstack/react-query";
import { getLaunchableNgcApiKeySpec } from "../utils";

interface PublishLaunchableModalProps {
  isOpen: boolean;
  onClose: () => void;
  launchableId: string;
  launchableName: string;
  orgId: string;
  description?: string;
  readmeLink?: string;
  launchableData: LaunchableFromList;
  onSuccess?: () => void;
}

// Reusable MissingFieldsPrompt component
const MissingFieldsPrompt: React.FC<{
  missingDescription: boolean;
  missingReadmeLink: boolean;
  localDescription: string;
  localReadmeLink: string;
  onDescriptionChange: (value: string) => void;
  onReadmeLinkChange: (value: string) => void;
}> = React.memo(
  ({
    missingDescription,
    missingReadmeLink,
    localDescription,
    localReadmeLink,
    onDescriptionChange,
    onReadmeLinkChange,
  }) => (
    <div className="mb-3 border border-amber-200/40 rounded-md p-4 bg-amber-400/5">
      <p className="text-sm font-medium text-amber-900 dark:text-amber-200">
        <span className="flex items-center gap-x-1">
          <AlertCircleIcon className="h-4 w-4 text-amber-900 dark:text-amber-200" />
          Missing{" "}
          {missingDescription && missingReadmeLink
            ? "description and README"
            : missingDescription
              ? "description"
              : "README"}
        </span>
      </p>
      <p className="text-xs text-amber-800 dark:text-amber-300 mt-1">
        Adding {missingDescription && missingReadmeLink ? "these" : "this"} will
        help users discover and understand your launchable
      </p>

      <div className="space-y-3 mt-3">
        {missingDescription && (
          <div>
            <label className="block text-xs font-medium text-primary mb-1.5">
              Add a description (recommended)
            </label>
            <Textarea
              value={localDescription}
              onChange={(e) => onDescriptionChange(e.target.value)}
              placeholder="Describe what your launchable does..."
              rows={3}
            />
          </div>
        )}

        {missingReadmeLink && (
          <div>
            <label className="block text-xs font-medium text-primary mb-1.5">
              Add a README link (recommended)
            </label>
            <Input
              type="url"
              value={localReadmeLink}
              onChange={(e) => onReadmeLinkChange(e.target.value)}
              placeholder="Ex: https://github.com/username/repo/blob/main/README.md"
            />
            <p className="text-xs text-secondary mt-1">
              Link to a GitHub/GitLab repo or a direct link to a markdown file
            </p>
          </div>
        )}
      </div>
    </div>
  ),
);

MissingFieldsPrompt.displayName = "MissingFieldsPrompt";

export const PublishLaunchableModal: React.FC<PublishLaunchableModalProps> = ({
  isOpen,
  onClose,
  launchableId,
  launchableName,
  orgId,
  description,
  readmeLink,
  launchableData,
  onSuccess,
}) => {
  const queryClient = useQueryClient();
  const [publishing, setPublishing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [localDescription, setLocalDescription] = useState(description || "");
  const [localReadmeLink, setLocalReadmeLink] = useState(readmeLink || "");

  // Track the initial missing state to keep prompts visible during editing
  const [initiallyMissingDescription, setInitiallyMissingDescription] =
    useState(!description?.trim());
  const [initiallyMissingReadmeLink, setInitiallyMissingReadmeLink] = useState(
    !readmeLink?.trim(),
  );

  // Track previous isOpen state to only reset when modal transitions from closed to open
  const prevIsOpenRef = useRef(isOpen);

  // Update local state and reset initial missing flags only when modal opens (not during publishing)
  useEffect(() => {
    const wasOpenedJustNow = isOpen && !prevIsOpenRef.current;

    if (wasOpenedJustNow) {
      setLocalDescription(description || "");
      setLocalReadmeLink(readmeLink || "");
      setInitiallyMissingDescription(!description?.trim());
      setInitiallyMissingReadmeLink(!readmeLink?.trim());
      setError(null);
      setPublishing(false);
    }

    prevIsOpenRef.current = isOpen;
  }, [isOpen, description, readmeLink]);

  const hasMissingFields =
    initiallyMissingDescription || initiallyMissingReadmeLink;

  const handlePublish = async () => {
    setPublishing(true);
    setError(null);

    try {
      // Check if description or readme were changed
      const descriptionChanged =
        localDescription.trim() !== (description || "").trim();
      const readmeLinkChanged =
        localReadmeLink.trim() !== (readmeLink || "").trim();

      // If description or readme changed, update the launchable first
      if (descriptionChanged || readmeLinkChanged) {
        // Prepare file request if readme link was provided/changed
        let fileRequest: LaunchableFileRequest | null = null;
        if (localReadmeLink.trim()) {
          fileRequest = {
            url: localReadmeLink.trim(),
            path: "./", // Default path for non-verb builds
          };
        }

        // Build the modify request using existing launchable data
        // Sanitize buildRequest to exclude empty lifecycle scripts
        const buildRequest = {
          ...(launchableData.buildRequest || { ports: [] }),
        };

        // Only include lifeCycleScriptAttr if it has actual content
        if (buildRequest.vmBuild?.lifeCycleScriptAttr) {
          if (!buildRequest.vmBuild.lifeCycleScriptAttr.script) {
            // Remove empty lifecycle script to avoid backend error
            buildRequest.vmBuild = {
              ...buildRequest.vmBuild,
              lifeCycleScriptAttr: undefined,
            };
          }
        }

        const createWorkspaceRequest = {
          ...(launchableData.createWorkspaceRequest || {
            instanceType: "",
            cloudCredId: "",
            storage: "",
            firewallRules: [],
          }),
        };

        // Backend returns both cloudCredId and workspaceGroupId but accepts only one
        if (createWorkspaceRequest.cloudCredId) {
          createWorkspaceRequest.workspaceGroupId = undefined;
        }

        const modifyRequest: ModifyLaunchableRequest = {
          id: launchableId,
          description: localDescription.trim(),
          createWorkspaceRequest,
          buildRequest,
          file: fileRequest,
          ngcApiKeySpec: getLaunchableNgcApiKeySpec(launchableData),
        };

        // Update the launchable
        const updateRes = await agent.Launchables.editEnvironmentLaunchable(
          orgId,
          modifyRequest,
        );

        if (!updateRes.success) {
          setError(updateRes?.message || "Failed to update launchable details");
          setPublishing(false);
          return;
        }
      }

      // Update the access level to published
      const res = await agent.Launchables.editLaunchableAccess(orgId, {
        id: launchableId,
        viewAccess: "published",
      });

      if (res && res.success) {
        // Invalidate queries to refresh the UI - await to ensure data is fresh
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: ["launchables"] }),
          queryClient.invalidateQueries({ queryKey: ["marketplace-stats"] }),
          queryClient.invalidateQueries({ queryKey: ["launchable-details"] }),
        ]);

        // Call onSuccess callback and await if it returns a promise
        await onSuccess?.();

        toast.success("Launchable published successfully! 🎉", {
          description:
            "Thank you for sharing your launchable with the community!",
        });

        onClose();
      } else {
        setError(res?.message || "Failed to publish launchable");
        setPublishing(false);
      }
    } catch (err) {
      setError("An error occurred while publishing");
      setPublishing(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[540px] max-h-[90vh] overflow-y-auto p-8">
        <DialogHeader>
          <div>
            <DialogTitle className="text-primary pb-2 flex items-center gap-x-2">
              <RocketLaunchIcon className="h-6 w-6 text-highlight" />
              Publish {launchableName}
            </DialogTitle>
            <DialogDescription className="mt-1 text-secondary">
              Publishing makes your launchable discoverable to thousands of
              developers, helps you build reputation, and lets others solve
              similar problems faster.
            </DialogDescription>
          </div>
        </DialogHeader>

        <div className="space-y-4">
          {hasMissingFields && (
            <MissingFieldsPrompt
              missingDescription={initiallyMissingDescription}
              missingReadmeLink={initiallyMissingReadmeLink}
              localDescription={localDescription}
              localReadmeLink={localReadmeLink}
              onDescriptionChange={setLocalDescription}
              onReadmeLinkChange={setLocalReadmeLink}
            />
          )}

          <div className="space-y-3">
            <InfoBox variant="info" className="bg-secondaryBg">
              <p className="text-xs text-secondary">
                <span className="font-semibold">Important:</span> Ensure your
                code doesn't contain API keys, passwords, or other secrets.
              </p>
            </InfoBox>
          </div>

          {error && (
            <InfoBox variant="error">
              <p className="text-sm text-red-900 dark:text-red-400">{error}</p>
            </InfoBox>
          )}
        </div>

        <DialogFooter>
          <Button variant="secondary" onClick={onClose} disabled={publishing}>
            Cancel
          </Button>
          <Button onClick={handlePublish} disabled={publishing}>
            {publishing ? "Publishing..." : "Publish"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
